/**
 * Entrées d'un joueur pour un tick de simulation.
 *
 * Le même objet circule sur le réseau (phase 3) et alimente la simulation
 * locale : il ne contient QUE l'intention du joueur, jamais d'état dérivé.
 */
export interface PlayerInputs {
  /** Déplacement vers la gauche. */
  readonly left: boolean;
  /** Déplacement vers la droite. */
  readonly right: boolean;
  /** Saut (déclenché sur front montant). */
  readonly jump: boolean;
  /** Action spéciale du jeu — champ magnétique pour Magnetic Duo (phase 2). */
  readonly action: boolean;
  /**
   * Axe horizontal analogique dans [-1, 1] (joystick tactile). Absent ou nul,
   * la simulation retombe sur `left`/`right` à pleine vitesse — le clavier et
   * les solutions du solveur n'émettent que les booléens.
   */
  readonly moveX?: number | undefined;
}

/** Touches booléennes — les entrées sur lesquelles une ability peut se brancher. */
export type PlayerButton = 'left' | 'right' | 'jump' | 'action';

/**
 * Intention horizontale effective dans [-1, 1] : l'axe analogique s'il est
 * non nul, sinon les booléens. Un `moveX` à 0 laissé par un joystick relâché
 * ne masque donc jamais un clavier encore enfoncé (appareils 2-en-1).
 */
export function moveAxis(inputs: PlayerInputs): number {
  const analog = inputs.moveX ?? 0;
  if (analog !== 0) {
    return Math.max(-1, Math.min(1, analog));
  }
  return (inputs.right ? 1 : 0) - (inputs.left ? 1 : 0);
}

/** Index d'un joueur du duo : 0 = personnage `P`, 1 = personnage `M`. */
export type PlayerIndex = 0 | 1;

/** Les deux index de joueurs, dans l'ordre du duo. */
export const PLAYER_INDICES = [0, 1] as const satisfies readonly PlayerIndex[];

/** Entrées neutres (aucune touche enfoncée). */
export const NEUTRAL_INPUTS: PlayerInputs = Object.freeze({
  left: false,
  right: false,
  jump: false,
  action: false,
});

/** Paire d'entrées, une par joueur du duo. */
export type DuoInputs = readonly [PlayerInputs, PlayerInputs];

/** Paire d'entrées neutres. */
export function neutralDuoInputs(): DuoInputs {
  return [NEUTRAL_INPUTS, NEUTRAL_INPUTS];
}

/** Applique une modification partielle sur des entrées (immutable). */
export function withInputs(base: PlayerInputs, patch: Partial<PlayerInputs>): PlayerInputs {
  return {
    left: patch.left ?? base.left,
    right: patch.right ?? base.right,
    jump: patch.jump ?? base.jump,
    action: patch.action ?? base.action,
    moveX: patch.moveX ?? base.moveX,
  };
}
